import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import PublicNavbar from "../components/layout/PublicNavbar";
import Footer from "../components/layout/Footer";
import Seo from "../components/Seo";
import api from "../lib/api";
import { formatDate } from "../lib/datetime";

export default function BlogDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/blogs/${slug}`)
      .then((r) => setPost(r.data))
      .catch((e) => setError(e?.response?.status === 404 ? "not_found" : "error"))
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <div className="min-h-screen bg-cream text-ink" data-testid="blog-detail-page">
      <Seo
        title={post ? `${post.title} | VenturaZ Resources` : "VenturaZ Resources"}
        description={post?.excerpt}
        keywords={post?.sector ? `${post.sector}, VenturaZ, private capital, ${post.title}` : undefined}
        path={`/resources/${slug}`}
        type="article"
      />
      <PublicNavbar />
      <main>
        <article className="max-w-[860px] mx-auto px-6 lg:px-10 py-16">
          <Link to="/resources" className="text-xs font-mono uppercase tracking-widest text-emerald-800 hover:text-gold" data-testid="blog-back">
            ← All Resources
          </Link>
          {loading && (
            <div className="mt-12 space-y-4 animate-pulse">
              <div className="h-3 w-1/4 bg-navy/10 rounded" />
              <div className="h-10 w-3/4 bg-navy/10 rounded" />
              <div className="h-64 bg-navy/[0.06] rounded" />
            </div>
          )}
          {!loading && error === "not_found" && (
            <div className="mt-16 text-center">
              <div className="font-heading text-2xl mb-2">Article not found</div>
              <div className="text-ink-soft">This resource may have been unpublished. Head back to browse the latest.</div>
            </div>
          )}
          {!loading && post && (
            <>
              <div className="mt-8 flex items-center gap-3 text-xs font-mono uppercase tracking-widest">
                <span className="text-emerald-800">{post.tag || post.sector || "Insight"}</span>
                <span className="text-ink-muted">·</span>
                <span className="text-ink-muted">{post.read_minutes || 6} min read</span>
                {post.published_at && (
                  <>
                    <span className="text-ink-muted">·</span>
                    <span className="text-ink-muted">{formatDate(post.published_at)}</span>
                  </>
                )}
              </div>
              <h1 className="mt-4 font-display text-4xl lg:text-5xl leading-tight" data-testid="blog-title">
                {post.title}
              </h1>
              {post.excerpt && (
                <p className="mt-6 text-lg text-ink-soft leading-relaxed">{post.excerpt}</p>
              )}
              <div className="mt-10 cms-preview" data-testid="blog-body">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.body_md || ""}</ReactMarkdown>
              </div>
            </>
          )}
        </article>
      </main>
      <Footer />
    </div>
  );
}
